import { ChatMessage, ChatSession } from '@/types';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { cookiesService } from '../services/cookiesService';
import { useConfigStore } from './configStore';

type ChatState = {
  webhookUrl: string;
  setWebhookUrl: (webhookUrl: string) => void;

  isOpen: boolean;
  toggleOpen: () => void;
  setOpen: (value: boolean) => void;
  fullScreen: boolean;
  toggleFullScreen: () => void;
  setFullScreen: (value: boolean) => void;
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  addExternalMessage: (botMessage: ChatMessage['output'][0]) => void;
  addQuickAnswer: (question: string, answer: string) => void;
  checkIfLastMessageIsQuickAnswer: () => boolean;

  isGdprOpen: boolean;
  setGdprOpen: (value: boolean) => void;
  gdprShakeTrigger: number;
  triggerGdprShake: () => void;

  chatSession: ChatSession;
  setThreadId: (threadId: string | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;
  resetSession: () => void;

  fullScreenImage: {
    key: string;
    image: string;
  } | null;
  setFullScreenImage: (image: { key: string; image: string } | null) => void;

  showFeedbackAlert: boolean;
  setShowFeedbackAlert: (showFeedbackAlert: boolean) => void;

  showNegativeFeedbackMessageId: string | null;
  setShowNegativeFeedbackMessageId: (showNegativeFeedbackMessageId: string | null) => void;

  // New state for tracking seen content
  seenContexts: Map<string, string | undefined>;
  addSeenContext: (selector: string, description?: string) => void;
  clearSeenContexts: () => void;

  messageWhileOpen: string | null;
  setMessageWhileOpen: (messageWhileOpen: string | null) => void;

  showInactivityWarning: boolean;
  setShowInactivityWarning: (showInactivityWarning: boolean) => void;

  showInactivityReset: boolean;
  setShowInactivityReset: (showInactivityReset: boolean) => void;
};

export const useChatStore = create<ChatState>()(
  devtools(
    (set, get) => ({
      webhookUrl: '',
      setWebhookUrl: (webhookUrl: string) => set({ webhookUrl }),

      isOpen: false,
      toggleOpen: () => {
        get().setOpen(!get().isOpen);
      },
      setOpen: (value: boolean) => {
        const config = useConfigStore.getState().config;
        if (!!value && config.ui?.gdpr?.enabled && !cookiesService.hasUserMadeChoice()) {
          get().setGdprOpen(true);
          get().triggerGdprShake();
          return;
        }

        set({ isOpen: value }, false, 'setOpen');
      },

      isGdprOpen: false,
      setGdprOpen: (value: boolean) => set({ isGdprOpen: value }, false, 'setGdprOpen'),
      gdprShakeTrigger: 0,
      triggerGdprShake: () =>
        set(state => ({ gdprShakeTrigger: state.gdprShakeTrigger + 1 }), false, 'triggerGdprShake'),

      fullScreen: false,
      toggleFullScreen: () =>
        set(state => ({ fullScreen: !state.fullScreen }), false, 'toggleFullScreen'),
      setFullScreen: (value: boolean) => set({ fullScreen: value }, false, 'setFullScreen'),
      messages: [],
      addMessage: (message: ChatMessage) =>
        set(state => ({ messages: [...state.messages, message] }), false, 'addMessage'),

      addExternalMessage: (botMessage: ChatMessage['output'][0]) =>
        set(
          state => {
            const messageId = `external-${Date.now()}`;
            const newMessage: ChatMessage = {
              id: messageId,
              output: [botMessage],
              messageId: messageId,
              threadId: state.chatSession.threadId || '',
              timestamp: new Date().toISOString(),
            };
            return { messages: [...state.messages, newMessage] };
          },
          false,
          'addExternalMessage'
        ),

      addQuickAnswer: (question: string, answer: string) =>
        set(
          state => {
            const userMessageId = `user-${Date.now()}`;
            const botMessageId = `bot-${Date.now()}`;

            const userMessage: ChatMessage = {
              id: userMessageId,
              output: [
                {
                  role: 'user',
                  type: 'text',
                  content: question,
                },
              ],
              messageId: userMessageId,
              threadId: state.chatSession.threadId || '',
              timestamp: new Date().toISOString(),
            };

            const botMessage: ChatMessage = {
              id: botMessageId,
              output: [
                {
                  role: 'assistant',
                  type: 'text',
                  content: answer,
                },
              ],
              messageId: botMessageId,
              threadId: state.chatSession.threadId || '',
              timestamp: new Date().toISOString(),
              isQuickAnswer: true,
            };

            return { messages: [...state.messages, userMessage, botMessage] };
          },
          false,
          'addQuickAnswer'
        ),

      checkIfLastMessageIsQuickAnswer: () => {
        const messages = get().messages;
        const lastMessage = messages[messages.length - 1];
        return lastMessage?.isQuickAnswer || false;
      },

      chatSession: {
        threadId: null,
        isLoading: false,
        error: null,
      },

      setThreadId: (threadId: string | null) =>
        set(
          state => ({
            chatSession: { ...state.chatSession, threadId },
          }),
          false,
          'setThreadId'
        ),
      setLoading: (isLoading: boolean) =>
        set(
          state => ({
            chatSession: { ...state.chatSession, isLoading },
          }),
          false,
          'setLoading'
        ),
      setError: (error: Error | null) =>
        set(
          state => ({
            chatSession: { ...state.chatSession, error },
          }),
          false,
          'setError'
        ),
      resetSession: () =>
        set(
          () => {
            if (typeof window !== 'undefined') {
              window.localStorage.removeItem('threadId');
            }

            return {
              chatSession: { threadId: null, isLoading: false, error: null },
              messages: [],
              seenContexts: new Map(),
              showInactivityWarning: false,
              showInactivityReset: false,
              messageWhileOpen: null,
            };
          },
          false,
          'resetSession'
        ),
      fullScreenImage: null,
      setFullScreenImage: (image: { key: string; image: string } | null) =>
        set(() => ({ fullScreenImage: image }), false, 'setFullScreenImage'),

      showFeedbackAlert: false,
      setShowFeedbackAlert: (showFeedbackAlert: boolean) => {
        set(() => ({ showFeedbackAlert }), false, 'setShowFeedbackAlert');
        if (showFeedbackAlert) {
          setTimeout(() => {
            set(() => ({ showFeedbackAlert: false }), false, 'setShowFeedbackAlert');
          }, 7000);
        }
      },

      showNegativeFeedbackMessageId: null,
      setShowNegativeFeedbackMessageId: (showNegativeFeedbackMessageId: string | null) =>
        set(() => ({ showNegativeFeedbackMessageId }), false, 'setShowNegativeFeedbackMessageId'),

      // Initialize seen contexts set
      seenContexts: new Map<string, string | undefined>(),

      // Add a new context to the set
      addSeenContext: (selector: string, description?: string) =>
        set(
          state => ({
            seenContexts: new Map(state.seenContexts).set(selector, description),
          }),
          false,
          'addSeenContext'
        ),

      // Clear all seen contexts
      clearSeenContexts: () =>
        set(
          () => ({
            seenContexts: new Map(),
          }),
          false,
          'clearSeenContexts'
        ),

      messageWhileOpen: null,
      setMessageWhileOpen: (messageWhileOpen: string | null) =>
        set(() => ({ messageWhileOpen }), false, 'setMessageWhileOpen'),

      showInactivityWarning: false,
      setShowInactivityWarning: (showInactivityWarning: boolean) =>
        set(() => ({ showInactivityWarning }), false, 'setShowInactivityWarning'),

      showInactivityReset: false,
      setShowInactivityReset: (showInactivityReset: boolean) =>
        set(() => ({ showInactivityReset }), false, 'setShowInactivityReset'),
    }),
    {
      name: 'chat-store',
      enabled: true,
    }
  )
);

// Helper function to get last quick answer context
export const getLastQuickAnswerContext = (): { question: string; answer: string } | null => {
  const messages = useChatStore.getState().messages;

  const lastMessage = messages[messages.length - 2];

  if (lastMessage?.isQuickAnswer) {
    const userMessage = messages[messages.length - 3];
    return {
      question: userMessage.output[0].content as string,
      answer: lastMessage.output[0].content as string,
    };
  }

  return null;
};
